type Props = {
  nomeHomenageado: string;
  valorFinal: number;
  valorBase: number;
  cupomCodigo: string | null;
  descontoPct: number | null;
};

function fmt(v: number) {
  return v.toFixed(2).replace('.', ',');
}

export default function PriceMicroCard({ nomeHomenageado, valorFinal, valorBase, cupomCodigo, descontoPct }: Props) {
  const hasDiscount = cupomCodigo && valorFinal < valorBase - 0.01;

  return (
    <div className="mz-pmc">
      <div className="mz-pmc-tag" aria-hidden>
        <span className="mz-pmc-stamp" />
        <span className="mz-pmc-line" />
      </div>

      <div className="mz-pmc-inner">
        <div className="mz-pmc-text">
          <div className="mz-pmc-eyebrow">
            <span className="mz-pmc-sparkle">✦</span>
            <span>Sua música pra</span>
          </div>
          <div className="mz-pmc-name">{nomeHomenageado || 'sua pessoa'}</div>
          <div className="mz-pmc-ready">está prontinha pra você ouvir</div>
        </div>

        <div className="mz-pmc-price-block">
          {hasDiscount && (
            <div className="mz-pmc-base-strike">R$ {fmt(valorBase)}</div>
          )}
          <div className="mz-pmc-price">
            <span className="mz-pmc-currency">R$</span>
            <span className="mz-pmc-amount">{fmt(valorFinal).split(',')[0]}</span>
            <span className="mz-pmc-cents">,{fmt(valorFinal).split(',')[1]}</span>
          </div>
          {hasDiscount && descontoPct != null && (
            <div className="mz-pmc-chip">
              <span>−{descontoPct}%</span>
              <span className="mz-pmc-chip-sep">·</span>
              <span className="mz-pmc-chip-code">{cupomCodigo}</span>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .mz-pmc {
          position: relative;
          margin: 0 16px 18px;
          padding: 18px 18px 16px;
          background:
            linear-gradient(180deg, #fffaf4 0%, #fff3e6 100%);
          border: 1.5px solid var(--gold-border);
          border-radius: 14px;
          box-shadow:
            0 6px 20px -8px rgba(204,102,32,0.18),
            0 1px 0 rgba(255,255,255,0.95) inset;
          overflow: hidden;
        }

        html[data-theme='dark'] .mz-pmc {
          background:
            linear-gradient(180deg, rgba(255,145,77,0.08) 0%, rgba(255,145,77,0.12) 100%);
        }

        /* Decoração: pequena "abertura" tipo etiqueta no canto */
        .mz-pmc-tag {
          position: absolute;
          top: -7px;
          left: 24px;
          display: flex;
          align-items: center;
          gap: 6px;
          pointer-events: none;
        }
        .mz-pmc-stamp {
          width: 14px;
          height: 14px;
          background: var(--gold);
          border-radius: 50%;
          box-shadow: 0 0 0 3px var(--cream), 0 2px 4px rgba(204,102,32,0.4);
        }
        html[data-theme='dark'] .mz-pmc-stamp {
          box-shadow: 0 0 0 3px var(--warm-white), 0 2px 4px rgba(204,102,32,0.4);
        }
        .mz-pmc-line {
          width: 36px;
          height: 1.5px;
          background: var(--gold-border);
          opacity: 0.6;
        }

        .mz-pmc-inner {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 14px;
          align-items: center;
        }

        .mz-pmc-text {
          min-width: 0;
        }

        .mz-pmc-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 10.5px;
          font-weight: 700;
          color: var(--amber-deep);
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .mz-pmc-sparkle {
          font-size: 12px;
          color: var(--gold);
          line-height: 1;
        }

        .mz-pmc-name {
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-weight: 700;
          font-style: italic;
          font-size: clamp(22px, 5.5vw, 30px);
          line-height: 1.05;
          color: var(--heading-ink);
          letter-spacing: -0.025em;
          word-break: break-word;
        }

        .mz-pmc-ready {
          font-family: 'DM Sans', sans-serif;
          font-size: 12.5px;
          font-weight: 500;
          color: var(--text-muted);
          margin-top: 6px;
          line-height: 1.35;
        }

        .mz-pmc-price-block {
          text-align: right;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 5px;
        }

        .mz-pmc-base-strike {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-soft);
          text-decoration: line-through;
          font-variant-numeric: tabular-nums;
          opacity: 0.7;
        }

        .mz-pmc-price {
          display: inline-flex;
          align-items: baseline;
          gap: 2px;
          color: var(--amber-deep);
          font-family: 'DM Sans', sans-serif;
          font-variant-numeric: tabular-nums;
          line-height: 1;
        }
        .mz-pmc-currency {
          font-size: 12px;
          font-weight: 700;
          opacity: 0.7;
          margin-right: 3px;
          letter-spacing: 0.02em;
        }
        .mz-pmc-amount {
          font-size: 32px;
          font-weight: 800;
          letter-spacing: -0.04em;
        }
        .mz-pmc-cents {
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.02em;
        }

        .mz-pmc-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 9px;
          background: var(--gold);
          color: #fff;
          border-radius: 999px;
          font-family: 'DM Sans', sans-serif;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.06em;
          box-shadow: 0 2px 8px rgba(255,145,77,0.45);
        }
        .mz-pmc-chip-sep { opacity: 0.6; font-weight: 500; }
        .mz-pmc-chip-code { letter-spacing: 0.12em; }

        @media (min-width: 720px) {
          .mz-pmc { margin: 0 auto 22px; max-width: 660px; padding: 22px 24px 20px; }
          .mz-pmc-amount { font-size: 38px; }
        }
      `}</style>
    </div>
  );
}
